import { useCallback, useState } from "react";
import { FileUploadProps, UploadedFile } from "../types";

const useFileUpload = ({
  setFiles,
  acceptedFileTypes = [],
  maxFileSize = 10 * 1024 * 1024,
  maxFiles = 100,
  onDeleteFile
}: FileUploadProps) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const validateFile = (
    file: File
  ): { isValid: boolean; errorMessage?: string } => {
    if (acceptedFileTypes.length > 0) {
      // Check if file type is accepted by MIME type or file extension
      const isAcceptedByMimeType = acceptedFileTypes.includes(file.type);
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));
      const isAcceptedByExtension = acceptedFileTypes.some(
        (type) => type.startsWith(".") && type.toLowerCase() === fileExtension
      );

      if (!isAcceptedByMimeType && !isAcceptedByExtension) {
        return { isValid: false, errorMessage: "File type not accepted" };
      }
    }
    if (file.size > maxFileSize) {
      return {
        isValid: false,
        errorMessage: `File size exceeds the limit of ${formatFileSize(
          maxFileSize
        )}`,
      };
    }
    return { isValid: true };
  };

  const processFiles = useCallback(
    (fileList: FileList) => {
      const files = Array.from(fileList);
      const newValidFiles: File[] = [];
      const newUploadedFiles: UploadedFile[] = [];

      files.forEach((file) => {
        const { isValid, errorMessage } = validateFile(file);
        const uploadedFile: UploadedFile = {
          id: crypto.randomUUID(),
          uploadStatus: isValid ? "uploading" : "error",
          name: file.name,
          type: file.type,
          size: file.size,
          lastModified: file.lastModified,
          errorMessage: errorMessage,
          preview: null,
        };

        newUploadedFiles.push(uploadedFile);
        if (isValid) {
          newValidFiles.push(file);
          if (
            file.type.startsWith("image/") ||
            file.type === "application/pdf"
          ) {
            const reader = new FileReader();
            reader.onload = (e: ProgressEvent<FileReader>) => {
              setUploadedFiles((prev) =>
                prev.map((f) =>
                  f.id === uploadedFile.id
                    ? { ...f, preview: e.target!.result as string }
                    : f
                )
              );
            };
            reader.readAsDataURL(file);
          }
        }
      });

      setUploadedFiles((prev) => {
        const totalFiles = prev.length + newUploadedFiles.length;
        if (totalFiles > maxFiles) {
          setFileError(
            `Maximum ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed`
          );
          return prev;
        }
        return [...prev, ...newUploadedFiles];
      });

      // Simulate file upload for valid files
      newUploadedFiles.forEach((file) => {
        if (file.uploadStatus === "uploading") {
          setTimeout(() => {
            setUploadedFiles((prev) =>
              prev.map((f) =>
                f.id === file.id ? { ...f, uploadStatus: "success" } : f
              )
            );
          }, 2000);
        }
      });

      // Only pass valid files to the parent component
      setFiles(newValidFiles);
    },
    [setFiles, uploadedFiles, acceptedFileTypes, maxFileSize, maxFiles]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
    },
    [processFiles]
  );

  const handleDeleteFile = useCallback(
    (id: string) => {
      const fileToDelete = uploadedFiles.find((file) => file.id === id);
      if (fileToDelete) {
        setUploadedFiles((prev) => prev.filter((file) => file.id !== id));
        onDeleteFile(fileToDelete);
        // Reset the file input
        const fileInput = document.getElementById(
          "actual-btn"
        ) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = "";
        }
      }
    },
    [uploadedFiles, onDeleteFile]
  );

  return { uploadedFiles, setUploadedFiles, fileError, handleFileChange,formatFileSize, handleDeleteFile, processFiles };
};

export default useFileUpload;